import { Calendar, Globe, Search, User } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import {
	generateMatchesForAlert,
	type ScoreLevel,
	type WatchlistMatch,
} from '@/data/aml/mockData';
import { cn } from '@/lib/utils';

function scoreClass(level: ScoreLevel): string {
	if (level === 'very_high' || level === 'high')
		return 'bg-red-50 text-red-700';
	if (level === 'medium') return 'bg-orange-50 text-orange-700';
	return 'bg-green-50 text-green-700';
}

type FormState = {
	customer_name: string;
	customer_id: string;
	customer_type: string;
	gender: string;
	date_of_birth: string;
	nationality: string;
	city: string;
	address_country: string;
};

const INITIAL_FORM: FormState = {
	customer_name: '',
	customer_id: '',
	customer_type: '',
	gender: '',
	date_of_birth: '',
	nationality: '',
	city: '',
	address_country: '',
};

function ProfileField({
	icon: _Icon,
	label,
	value,
}: {
	icon: React.ElementType;
	label: string;
	value: string;
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs text-[#737373]">{label}</span>
			<span className="text-sm font-medium text-[#0A0A0A]">{value || '—'}</span>
		</div>
	);
}

export default function ManualScreeningPage() {
	const [form, setForm] = useState<FormState>(INITIAL_FORM);
	const [results, setResults] = useState<WatchlistMatch[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);

	const selectedMatch = results?.find((m) => m.id === selectedMatchId) ?? null;

	const handleScreen = () => {
		if (!form.customer_name.trim()) return;
		setLoading(true);
		setResults(null);
		setSelectedMatchId(null);
		setTimeout(() => {
			const fakeId = `manual_${form.customer_name.slice(0, 3)}`;
			const matches = generateMatchesForAlert(fakeId);
			setResults(matches);
			setSelectedMatchId(matches[0]?.id ?? null);
			setLoading(false);
		}, 800);
	};

	const handleClear = () => {
		setForm(INITIAL_FORM);
		setResults(null);
		setSelectedMatchId(null);
	};

	const set =
		(key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
			setForm((prev) => ({ ...prev, [key]: e.target.value }));

	return (
		<div className="min-h-screen bg-background">
			<div className="border-b border-border bg-white px-6 py-4">
				<h1 className="text-xl font-semibold text-[#0A0A0A]">
					Manual Screening
				</h1>
				<p className="text-sm text-[#737373] mt-0.5">
					Screen an individual or entity against the watchlist
				</p>
			</div>

			<div className="p-6 space-y-6 max-w-[1200px]">
				{/* Form */}
				<Card className="border border-[#E5E5E5]">
					<CardHeader className="pb-3">
						<CardTitle className="text-base font-semibold text-[#0A0A0A]">
							Subject Details
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">
									Full Name <span className="text-red-500">*</span>
								</Label>
								<Input
									value={form.customer_name}
									onChange={set('customer_name')}
									placeholder="e.g. John Smith"
								/>
							</div>
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">Customer ID</Label>
								<Input
									value={form.customer_id}
									onChange={set('customer_id')}
									placeholder="e.g. CUST-12345"
								/>
							</div>
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">Type</Label>
								<Select
									value={form.customer_type}
									onValueChange={(v) =>
										setForm((p) => ({ ...p, customer_type: v }))
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select type" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="individual">Individual</SelectItem>
										<SelectItem value="corporate">Corporate</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">Gender</Label>
								<Select
									value={form.gender}
									onValueChange={(v) => setForm((p) => ({ ...p, gender: v }))}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select gender" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="male">Male</SelectItem>
										<SelectItem value="female">Female</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">Date of Birth</Label>
								<Input
									type="date"
									value={form.date_of_birth}
									onChange={set('date_of_birth')}
								/>
							</div>
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">Nationality</Label>
								<Input
									value={form.nationality}
									onChange={set('nationality')}
									placeholder="e.g. United States"
								/>
							</div>
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">City</Label>
								<Input
									value={form.city}
									onChange={set('city')}
									placeholder="e.g. New York"
								/>
							</div>
							<div className="space-y-1.5">
								<Label className="text-sm font-medium">
									Country of Address
								</Label>
								<Input
									value={form.address_country}
									onChange={set('address_country')}
									placeholder="e.g. United States"
								/>
							</div>
						</div>
						<div className="mt-5 flex items-center gap-3">
							<Button
								className="gap-2 bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
								disabled={!form.customer_name.trim() || loading}
								onClick={handleScreen}
							>
								<Search className="h-4 w-4" />
								{loading ? 'Screening...' : 'Screen'}
							</Button>
							<Button variant="outline" onClick={handleClear}>
								Clear
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* Loading skeleton */}
				{loading && (
					<Card className="border border-[#E5E5E5]">
						<CardContent className="p-4 space-y-3">
							<Skeleton className="h-5 w-40" />
							<Skeleton className="h-12 w-full rounded-md" />
							<Skeleton className="h-12 w-full rounded-md" />
							<Skeleton className="h-12 w-full rounded-md" />
						</CardContent>
					</Card>
				)}

				{/* Results */}
				{!loading && results !== null && (
					<>
						<Card className="border border-[#E5E5E5]">
							<CardContent className="p-4">
								<div className="mb-3 flex items-center justify-between">
									<div className="text-sm font-semibold text-[#0A0A0A]">
										Screening Results — {results.length}{' '}
										{results.length === 1 ? 'match' : 'matches'} found
									</div>
									<Badge
										variant={results.length > 0 ? 'destructive' : 'secondary'}
									>
										{results.length > 0 ? 'Matches Found' : 'No Matches'}
									</Badge>
								</div>
								{results.length === 0 ? (
									<div className="rounded-xl border border-dashed border-[#D4D4D4] p-8 text-center text-sm text-[#737373]">
										No watchlist matches found for this subject.
									</div>
								) : (
									<div className="rounded-md border border-[#E5E5E5] overflow-hidden">
										<Table>
											<TableHeader>
												<TableRow className="bg-[#FAFAFA]">
													<TableHead className="text-xs font-semibold text-[#737373]">
														Name
													</TableHead>
													<TableHead className="text-xs font-semibold text-[#737373]">
														Score
													</TableHead>
													<TableHead className="text-xs font-semibold text-[#737373]">
														Category
													</TableHead>
													<TableHead className="text-xs font-semibold text-[#737373]">
														Nationality
													</TableHead>
													<TableHead className="text-xs font-semibold text-[#737373]">
														Source
													</TableHead>
													<TableHead className="text-xs font-semibold text-[#737373]">
														PEP
													</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{results.map((m) => (
													<TableRow
														key={m.id}
														className={cn(
															'cursor-pointer transition-colors',
															selectedMatchId === m.id
																? 'bg-blue-50'
																: 'hover:bg-[#FAFAFA]',
														)}
														onClick={() =>
															setSelectedMatchId(
																m.id === selectedMatchId ? null : m.id,
															)
														}
													>
														<TableCell className="text-sm font-medium text-[#0A0A0A]">
															{m.name}
														</TableCell>
														<TableCell>
															<span
																className={cn(
																	'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
																	scoreClass(m.match_level),
																)}
															>
																{m.score}%
															</span>
														</TableCell>
														<TableCell>
															<span className="text-xs text-[#525252]">
																{m.category}
															</span>
														</TableCell>
														<TableCell>
															<span className="text-xs text-[#525252]">
																{m.nationality}
															</span>
														</TableCell>
														<TableCell>
															<span className="text-xs text-[#525252]">
																{m.source}
															</span>
														</TableCell>
														<TableCell>
															<Badge
																variant={
																	m.pep_status === 'PEP'
																		? 'destructive'
																		: 'secondary'
																}
																className="text-xs"
															>
																{m.pep_status}
															</Badge>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Selected Match Detail */}
						{selectedMatch && (
							<Card className="border border-[#E5E5E5]">
								<CardContent className="p-4">
									<div className="mb-3 text-sm font-semibold text-[#0A0A0A]">
										Match Detail — {selectedMatch.name}
										<span
											className={cn(
												'ml-2 inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
												scoreClass(selectedMatch.match_level),
											)}
										>
											{selectedMatch.score}% match
										</span>
									</div>
									<div className="grid grid-cols-2 gap-3 md:grid-cols-3">
										<ProfileField
											icon={User}
											label="Name"
											value={selectedMatch.name}
										/>
										<ProfileField
											icon={Calendar}
											label="Date of Birth"
											value={selectedMatch.dob}
										/>
										<ProfileField
											icon={Globe}
											label="Nationality"
											value={selectedMatch.nationality}
										/>
										<ProfileField
											icon={User}
											label="Category"
											value={selectedMatch.category}
										/>
										<ProfileField
											icon={User}
											label="PEP Status"
											value={selectedMatch.pep_status}
										/>
										<ProfileField
											icon={User}
											label="Source"
											value={selectedMatch.source}
										/>
									</div>
									{selectedMatch.aliases.length > 0 && (
										<div className="mt-3">
											<span className="text-xs text-[#737373]">Aliases</span>
											<div className="mt-1 flex flex-wrap gap-1">
												{selectedMatch.aliases.map((a) => (
													<span
														key={a}
														className="inline-flex items-center rounded-full bg-[#F5F5F5] px-2.5 py-0.5 text-xs text-[#525252]"
													>
														{a}
													</span>
												))}
											</div>
										</div>
									)}
								</CardContent>
							</Card>
						)}
					</>
				)}
			</div>
		</div>
	);
}
